import * as BABYLON from '@babylonjs/core'
import { buildFurnitureMesh } from './buildFurniture'
import { FurnitureKind, RoomPresetId, ROOM_PRESETS } from './roomPresets'
import { createDisposerBag } from '../../sceneKit/disposerBag'
import { buildTemplatedRoomShell } from '../../sceneKit/roomTemplate'
import { hangWallPrints } from '../../sceneKit/wallPrints'
import { buildOrbitCamera } from '../../sceneKit/orbitCamera'

export interface HallPhoto {
  url: string
  ratio: number
}

export interface FurnitureItemInfo {
  id: string
  kind: FurnitureKind
}

export interface SpacesRoomCallbacks {
  onItemsChange?: (items: FurnitureItemInfo[]) => void
  onSelectChange?: (id: string | null) => void
}

export interface SpacesRoomApi {
  setPreset: (preset: RoomPresetId) => void
  addFurniture: (kind: FurnitureKind) => void
  rotateSelected: () => void
  removeSelected: () => void
  dispose: () => void
}

interface FurnitureItem {
  id: string
  kind: FurnitureKind
  root: BABYLON.TransformNode
  meshes: BABYLON.Mesh[]
  footprint: { w: number; d: number }
}

let seq = 0
const nextId = () => `spaces-item-${++seq}`

/**
 * Builds one room (walls/floor/ceiling + a few photos on the back wall) and
 * owns everything placed in it: add a piece from the catalog, click to
 * select, drag to move it across the floor, R to rotate, Delete to remove.
 * Switching preset tears the whole thing down and rebuilds fresh — simplest
 * way to keep room shell and furniture defaults in sync. Shell/prints/camera
 * come from the shared sceneKit; furniture placement stays bespoke here.
 */
export function buildSpacesRoom(
  scene: BABYLON.Scene,
  canvas: HTMLCanvasElement,
  initialPreset: RoomPresetId,
  photos: HallPhoto[],
  callbacks: SpacesRoomCallbacks = {},
): SpacesRoomApi {
  const bag = createDisposerBag()
  let items: FurnitureItem[] = []
  let selectedId: string | null = null
  let highlight: BABYLON.HighlightLayer | null = null
  let dragging: { item: FurnitureItem; plane: BABYLON.Plane } | null = null

  const emitItems = () => callbacks.onItemsChange?.(items.map((i) => ({ id: i.id, kind: i.kind })))
  const emitSelect = () => callbacks.onSelectChange?.(selectedId)

  function clearAll() {
    bag.disposeAll()
    items = []
    selectedId = null
    dragging = null
  }

  function selectItem(id: string | null) {
    selectedId = id
    highlight?.removeAllMeshes()
    if (id) {
      const item = items.find((i) => i.id === id)
      item?.meshes.forEach((m) => highlight?.addMesh(m, BABYLON.Color3.FromHexString('#be9dfd')))
    }
    emitSelect()
  }

  function removeItem(id: string) {
    const item = items.find((i) => i.id === id)
    if (!item) return
    item.meshes.forEach((m) => m.dispose())
    item.root.dispose()
    items = items.filter((i) => i.id !== id)
    if (selectedId === id) selectItem(null)
    emitItems()
  }

  function spawnFurniture(kind: FurnitureKind, x: number, z: number, rotY: number) {
    const built = buildFurnitureMesh(scene, kind, ++seq)
    built.root.position.set(x, 0, z)
    built.root.rotation.y = rotY
    const id = nextId()
    built.meshes.forEach((m) => {
      m.metadata = { spacesItemId: id }
    })
    const item: FurnitureItem = { id, kind, root: built.root, meshes: built.meshes, footprint: built.footprint }
    items.push(item)
    return item
  }

  function build(preset: RoomPresetId) {
    clearAll()
    const geo = ROOM_PRESETS[preset]
    const { width: W, height: H, depth: D } = geo.template

    scene.clearColor = BABYLON.Color4.FromHexString('#0b0a10ff')
    scene.fogMode = BABYLON.Scene.FOGMODE_EXP2
    scene.fogDensity = 0.0035
    scene.fogColor = BABYLON.Color3.FromHexString('#15121a')

    buildTemplatedRoomShell(scene, bag, geo.template)

    const ambient = bag.track(new BABYLON.HemisphericLight('spaces-ambient', new BABYLON.Vector3(0, 1, 0), scene))
    ambient.intensity = 1.35
    ambient.diffuse = BABYLON.Color3.FromHexString(geo.ambientColor)
    ambient.groundColor = BABYLON.Color3.FromHexString(geo.ambientGround)
    const fill = bag.track(new BABYLON.PointLight('spaces-fill', new BABYLON.Vector3(0, H - 0.4, D / 2), scene))
    fill.diffuse = BABYLON.Color3.FromHexString(geo.ambientColor)
    fill.intensity = 0.7
    fill.range = Math.max(W, D) * 1.5
    const fill2 = bag.track(new BABYLON.PointLight('spaces-fill-2', new BABYLON.Vector3(0, H - 0.4, -D / 2), scene))
    fill2.diffuse = BABYLON.Color3.FromHexString(geo.ambientColor)
    fill2.intensity = 0.45
    fill2.range = Math.max(W, D) * 1.5

    // A handful of photos on the back wall — real gallery arts double as decor here too.
    const framePhotos = photos.slice(0, 4)
    const spread = Math.min(W - 1.2, framePhotos.length * 1.6)
    hangWallPrints(
      scene,
      bag,
      framePhotos,
      framePhotos.map((_photo, i) => {
        const step = framePhotos.length > 1 ? spread / (framePhotos.length - 1) : 0
        const x = framePhotos.length > 1 ? -spread / 2 + i * step : 0
        return { x, y: 2.1, z: D / 2, wallRotY: Math.PI, width: 1.15 }
      }),
    )

    highlight = bag.track(new BABYLON.HighlightLayer('spaces-highlight', scene, { blurHorizontalSize: 0.6, blurVerticalSize: 0.6 }))

    const { camera: cam } = buildOrbitCamera(scene, canvas, bag, {
      name: 'spaces-cam',
      alpha: -Math.PI / 2 - 0.5,
      beta: 1.28,
      radius: Math.max(W, D) * 0.6,
      target: new BABYLON.Vector3(0, 1, D * 0.1),
      roomHeight: H,
      radiusReach: Math.max(W, D) * 1.1,
    })

    const pipe = bag.track(new BABYLON.DefaultRenderingPipeline('spaces-pipe', true, scene, [cam]))
    pipe.fxaaEnabled = true
    pipe.imageProcessingEnabled = true
    pipe.imageProcessing.toneMappingEnabled = true
    pipe.imageProcessing.toneMappingType = BABYLON.ImageProcessingConfiguration.TONEMAPPING_ACES
    pipe.imageProcessing.exposure = 1.15
    pipe.imageProcessing.contrast = 1.05

    geo.furniture.forEach((seed) => spawnFurniture(seed.kind, seed.x, seed.z, seed.rotY))
    emitItems()
    emitSelect()

    const floorPlane = new BABYLON.Plane(0, -1, 0, 0)
    const bounds = { x: W / 2 - 0.3, z: D / 2 - 0.3 }

    const findItemFromMesh = (m: BABYLON.AbstractMesh | null): FurnitureItem | null => {
      const id = m?.metadata?.spacesItemId as string | undefined
      return id ? items.find((i) => i.id === id) || null : null
    }

    const onPointerDown = (e: PointerEvent) => {
      const pick = scene.pick(scene.pointerX, scene.pointerY)
      const item = pick.hit ? findItemFromMesh(pick.pickedMesh) : null
      if (item) {
        selectItem(item.id)
        cam.detachControl()
        dragging = { item, plane: floorPlane }
        e.preventDefault()
      } else {
        selectItem(null)
      }
    }
    const onPointerMove = () => {
      if (!dragging) return
      const ray = scene.createPickingRay(scene.pointerX, scene.pointerY, BABYLON.Matrix.Identity(), cam)
      const distance = ray.intersectsPlane(dragging.plane)
      if (distance == null) return
      const hit = ray.origin.add(ray.direction.scale(distance))
      dragging.item.root.position.x = Math.max(-bounds.x, Math.min(bounds.x, hit.x))
      dragging.item.root.position.z = Math.max(-bounds.z, Math.min(bounds.z, hit.z))
    }
    const onPointerUp = () => {
      if (!dragging) return
      dragging = null
      cam.attachControl(canvas, true)
    }
    canvas.addEventListener('pointerdown', onPointerDown)
    canvas.addEventListener('pointermove', onPointerMove)
    canvas.addEventListener('pointerup', onPointerUp)
    bag.defer(() => {
      canvas.removeEventListener('pointerdown', onPointerDown)
      canvas.removeEventListener('pointermove', onPointerMove)
      canvas.removeEventListener('pointerup', onPointerUp)
    })
  }

  build(initialPreset)

  return {
    setPreset: (preset) => build(preset),
    addFurniture: (kind) => {
      // Spawn near room center with a little jitter so repeated adds don't stack exactly on top of each other.
      const jitter = (items.length % 5) * 0.35 - 0.7
      spawnFurniture(kind, jitter, jitter * 0.6, 0)
      emitItems()
    },
    rotateSelected: () => {
      const item = items.find((i) => i.id === selectedId)
      if (item) item.root.rotation.y += Math.PI / 4
    },
    removeSelected: () => {
      if (selectedId) removeItem(selectedId)
    },
    dispose: () => {
      clearAll()
      highlight = null
    },
  }
}
